import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShoppingCart, ShieldCheck, Percent, Boxes, PlayCircle, Cpu } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CodeBox, FeatureCard, DemoHero, Callout, useDocMeta } from '../components/esl/EslShared';

const EslDemoNegocios = () => {
  useDocMeta(
    'BLOX ESL — Demo de reglas de negocio',
    'BLOX ESL como motor de reglas para pedidos: descuentos, impuestos, envío, aprobación y revisión manual. Políticas empresariales configurables desde un archivo .blox sin recompilar.'
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Header />

      <section className="border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <DemoHero
            eyebrow={<><ShoppingCart className="w-3 h-3" /> Demo 3 de 3</>}
            title="Demo de negocios"
            subtitle="BLOX ESL como motor de reglas para pedidos: aprobaciones, descuentos, impuestos, envío y revisión manual. El host C mantiene la persistencia, BLOX decide la política."
            accent="fuchsia"
          />
        </div>
      </section>

      {/* CONTEXTO */}
      <section className="py-12 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Contexto</h2>
          <p className="text-slate-400 text-base sm:text-lg mb-6 leading-relaxed">
            Las <span className="text-white">políticas comerciales</span> cambian por temporada, campaña, país o segmento. Tenerlas incrustadas en el binario obliga a redeployar. Con BLOX ESL viven en un archivo <code className="font-mono text-fuchsia-300">.blox</code> auditable.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FeatureCard icon={ShoppingCart} title="Pedidos en C">
              El host administra clientes, órdenes, persistencia, autenticación e integraciones (ERP, CRM, pasarela de pago).
            </FeatureCard>
            <FeatureCard icon={Percent} title="Reglas en BLOX">
              Descuentos, cupones, tramos, impuestos y umbrales de aprobación en <code className="font-mono text-fuchsia-300">reglas.blox</code>.
            </FeatureCard>
            <FeatureCard icon={ShieldCheck} title="Auditoría al host">
              BLOX llama <code className="font-mono text-fuchsia-300">HOST_AUDIT()</code>; el host registra la traza en su propio sistema.
            </FeatureCard>
          </div>
        </div>
      </section>

      {/* UBICACIÓN Y EJECUCIÓN */}
      <section className="py-12 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Ubicación y ejecución</h2>
          <CodeBox filename="ruta" lang="text">{`integrations/demo-reglas-C`}</CodeBox>
          <CodeBox filename="run.ps1" lang="powershell">{`.\\integrations\\demo-reglas-C\\run.ps1`}</CodeBox>
        </div>
      </section>

      {/* MAIN.C */}
      <section className="py-12 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">¿Qué hace <code className="font-mono text-fuchsia-300">main.c</code>?</h2>
          <p className="text-slate-400 mb-6 leading-relaxed">
            Define la estructura <code className="font-mono text-fuchsia-300">Order</code>, registra un callback de auditoría y evalúa cada pedido vía BLOX.
          </p>

          <CodeBox filename="main.c" lang="c">{`typedef struct Order {
    const char *id;
    const char *customer;
    const char *country;
    const char *coupon;
    double      subtotal;
    double      score;
} Order;`}</CodeBox>

          <p className="text-slate-300 text-sm mb-2">Callback nativo para auditoría:</p>
          <CodeBox filename="main.c" lang="c">{`blox_vm_register_native(
    vm,
    "HOST_AUDIT",
    2,
    BLOX_VALUE_EMPTY,
    host_audit,
    &ledger
);`}</CodeBox>

          <p className="text-slate-300 text-sm mb-2">Cada orden se serializa a JSON y se convierte en tabla BLOX:</p>
          <CodeBox filename="pedido.json" lang="json">{`[{
  "id": "A104",
  "cliente": "NORMAL",
  "pais": "PY",
  "cupon": "",
  "subtotal": 6300.00,
  "score_credito": 640
}]`}</CodeBox>

          <CodeBox filename="main.c" lang="c">{`blox_vm_table_from_json(vm, json_view, &input);
blox_vm_call(vm, "evaluar_orden", &input, 1, &result);`}</CodeBox>

          <p className="text-slate-300 text-sm mt-4">El resultado es una tabla de una fila con campos:</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
            {['aprobada', 'requiere_revision', 'descuento_pct', 'impuesto', 'envio', 'total', 'mensaje'].map(id => (
              <code key={id} className="px-3 py-2 rounded-lg bg-fuchsia-500/5 border border-fuchsia-500/20 text-fuchsia-300 font-mono text-xs text-center">
                {id}
              </code>
            ))}
          </div>
        </div>
      </section>

      {/* REGLAS.BLOX */}
      <section className="py-12 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">¿Qué hace <code className="font-mono text-fuchsia-300">reglas.blox</code>?</h2>
          <p className="text-slate-400 mb-6 leading-relaxed">
            Expone <code className="font-mono text-fuchsia-300">evaluar_orden(pedido)</code>. Lee campos del pedido, aplica descuentos por segmento y umbrales de aprobación por score de crédito.
          </p>

          <CodeBox filename="reglas.blox" lang="blox">{`FUNCION evaluar_orden(pedido)
INICIO
    NUMERICO subtotal, score, descuento, impuesto, envio, total
    CADENA cliente, pais, cupon, mensaje
    LOGICO aprobada, revision

    subtotal = pedido[1].subtotal
    score    = pedido[1].score_credito
    cliente  = pedido[1].cliente
    pais     = pedido[1].pais
    cupon    = pedido[1].cupon`}</CodeBox>

          <p className="text-slate-300 text-sm mb-2">Descuentos por segmento:</p>
          <CodeBox filename="reglas.blox" lang="blox">{`SI (cliente == "VIP") ENTONCES
    descuento = 0.15
    envio     = 0
FIN_SI

SI (cupon == "BLACKFRIDAY") ENTONCES
    descuento = descuento + 0.10
FIN_SI`}</CodeBox>

          <p className="text-slate-300 text-sm mb-2">Aprobación por monto y score:</p>
          <CodeBox filename="reglas.blox" lang="blox">{`SI (subtotal > 5000) ENTONCES
    SI (score < 700) ENTONCES
        aprobada = FALSO
        revision = VERDADERO
        mensaje  = "requiere aprobacion manual"
        HOST_AUDIT("revision", "monto alto con score medio")
    FIN_SI
FIN_SI`}</CodeBox>

          <p className="text-slate-300 text-sm mb-2">Devuelve una tabla con la decisión completa:</p>
          <CodeBox filename="reglas.blox" lang="blox">{`resultado = {
    aprobada          = aprobada,
    requiere_revision = revision,
    descuento_pct     = descuento,
    impuesto          = impuesto,
    envio             = envio,
    total             = total,
    mensaje           = mensaje
}

RETORNAR resultado
FINAL_FUNCION`}</CodeBox>

          <Callout tone="cyan" label="Resultado esperado">
            <code className="font-mono text-fuchsia-300">Resumen reglas: auditorias=3 rechazadas=2 total_aprobado=4204.00</code>
            <p className="mt-2 text-slate-300">
              El área comercial puede ajustar descuentos, cupones y umbrales editando <code className="font-mono text-fuchsia-300">reglas.blox</code>; el host C sigue en producción sin recompilar.
            </p>
          </Callout>
        </div>
      </section>

      {/* CTA cross-demo */}
      <section className="pt-16 pb-32 sm:pb-24 lg:pb-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Boxes className="w-12 h-12 text-fuchsia-400 mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Explora las otras integraciones
          </h2>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/embedded-scripting-language/demo-godot"
              data-testid="negocios-to-godot"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 font-semibold hover:bg-slate-700 transition-colors"
            >
              <PlayCircle className="w-4 h-4" />
              Demo Godot
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/embedded-scripting-language/demo-industrial"
              data-testid="negocios-to-industrial"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 font-semibold hover:bg-slate-700 transition-colors"
            >
              <Cpu className="w-4 h-4" />
              Demo industrial
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EslDemoNegocios;
